// components/IEP/QuickActions.js
import React from 'react';
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress
} from '@mui/material';
import {
  RocketLaunch,
  Psychology,
  Timeline,
  ShowChart,
  Assessment,
  Download,
  Share,
  Add,
  Refresh,
  TrendingUp,
  Warning,
  CheckCircle,
  ArrowForward,
  AutoGraph,
  CompareArrows
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useIEP } from '../../contexts/IEPContext';

const QuickActions = ({ organization }) => {
  const navigate = useNavigate();
  const { iepEngine, recommendations, roadmap } = useIEP();

  const quickActions = [
    {
      id: 'run-ai-optimization',
      title: 'Run AI Optimization',
      description: 'Let Q* AI optimize your transformation sequence',
      icon: <Psychology />,
      color: '#6366f1',
      action: () => navigate('/iep/ai-optimizer'),
      badge: 'Q*'
    },
    {
      id: 'create-simulation',
      title: 'Create Simulation',
      description: 'Run what-if analysis on your roadmap',
      icon: <ShowChart />,
      color: '#10b981',
      action: () => navigate('/iep/simulations'),
      badge: 'AI'
    },
    {
      id: 'export-roadmap',
      title: 'Export Roadmap',
      description: 'Download your transformation plan',
      icon: <Download />,
      color: '#f59e0b',
      action: () => {
        // Export logic would go here
        console.log('Exporting roadmap');
      }
    },
    {
      id: 'share-dashboard',
      title: 'Share Dashboard',
      description: 'Share progress with stakeholders',
      icon: <Share />,
      color: '#8b5cf6',
      action: () => {
        // Share logic would go here
        console.log('Sharing dashboard');
      }
    }
  ];

  const suggestedActions = [
    {
      id: 'review-critical',
      title: 'Review Critical Transformations',
      description: `${recommendations?.filter(r => r.priority === 'critical').length || 0} pending`,
      icon: <Warning color="error" />,
      action: () => navigate('/iep/transformations?priority=critical'),
      priority: 'high'
    },
    {
      id: 'update-budget',
      title: 'Update Budget Allocation',
      description: 'Adjust resources based on new constraints',
      icon: <TrendingUp color="success" />,
      action: () => navigate('/iep/roadmap'),
      priority: 'medium'
    },
    {
      id: 'run-qstar',
      title: 'Run Q* Re-optimization',
      description: 'AI found better sequence available',
      icon: <AutoGraph color="secondary" />,
      action: () => navigate('/iep/ai-optimizer'),
      priority: 'high'
    }
  ];

  const recentActivities = [
    { id: 1, text: 'T7 - Process Intelligence Transformation added to roadmap', time: '2 hours ago' },
    { id: 2, text: 'AI optimization improved ROI by 15%', time: '1 day ago' },
    { id: 3, text: 'Dimension scores updated for Strategic Intelligence', time: '2 days ago' },
    { id: 4, text: 'New simulation scenario created: "Aggressive Growth"', time: '3 days ago' },
  ];

  const handleQuickAction = (action) => {
    if (typeof action === 'function') {
      action();
    } else {
      navigate(action);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Quick Actions
      </Typography>
      
      {/* Quick Action Buttons */}
      <Grid container spacing={2} mb={3}>
        {quickActions.map((action) => (
          <Grid item xs={12} sm={6} md={3} key={action.id}>
            <Card 
              elevation={2}
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s',
                border: `1px solid ${action.color}30`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${action.color}20`,
                  border: `1px solid ${action.color}60`
                }
              }}
              onClick={action.action}
            >
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: `${action.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    color: action.color
                  }}
                >
                  {action.icon}
                </Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="600">
                  {action.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {action.description}
                </Typography>
                {action.badge && (
                  <Chip 
                    label={action.badge}
                    size="small"
                    sx={{ 
                      mt: 1,
                      bgcolor: `${action.color}15`,
                      color: action.color,
                      border: `1px solid ${action.color}30`
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Suggested Actions */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight="600">
              Suggested Actions
            </Typography>
            <Button 
              size="small" 
              variant="text" 
              endIcon={<ArrowForward />}
              onClick={() => navigate('/iep/dashboard')}
            >
              View All
            </Button>
          </Box>
          
          <List dense>
            {suggestedActions.map((action) => (
              <ListItem 
                key={action.id}
                button
                onClick={action.action}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <ListItemIcon>
                  {action.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={action.title}
                  secondary={action.description}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: action.priority === 'high' ? 600 : 400
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption'
                  }}
                />
                <Chip 
                  label={action.priority}
                  size="small"
                  color={action.priority === 'high' ? 'error' : 'warning'}
                  sx={{ ml: 1 }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card elevation={2}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight="600">
              Recent Activity
            </Typography>
            <IconButton size="small">
              <Refresh />
            </IconButton>
          </Box>
          
          <List dense>
            {recentActivities.map((activity, index) => (
              <React.Fragment key={activity.id}>
                <ListItem 
                  sx={{ 
                    py: 1,
                    '&:hover': {
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main'
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body2">
                        {activity.text}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {activity.time}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < recentActivities.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
          
          <Button 
            fullWidth 
            variant="outlined" 
            size="small"
            sx={{ mt: 2 }}
            onClick={() => navigate('/iep/dashboard')}
          >
            View Activity Log
          </Button>
        </CardContent>
      </Card>

      {/* Roadmap Progress */}
      {roadmap && (
        <Card elevation={2} sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Roadmap Progress
            </Typography>
            
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Phase 1: Quick Wins
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  30%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={30}
                sx={{ height: 6, borderRadius: 3 }}
                color="primary"
              />
            </Box>
            
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Phase 2: Strategic Transformation
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  10%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={10}
                sx={{ height: 6, borderRadius: 3 }}
                color="success"
              />
            </Box>
            
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Phase 3: Optimization & Scaling
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  0%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={0}
                sx={{ height: 6, borderRadius: 3 }}
                color="warning"
              />
            </Box>
            
            <Button 
              fullWidth 
              variant="contained" 
              startIcon={<Timeline />}
              onClick={() => navigate('/iep/roadmap')}
            >
              View Full Roadmap
            </Button>
          </CardContent>
        </Card>
      )}

      {/* AI Insights */}
      <Card 
        elevation={2}
        sx={{ 
          mt: 3,
          background: 'linear-gradient(145deg, #6366f110, #8b5cf610)',
          border: '1px solid',
          borderColor: 'primary.light'
        }}
      >
        <CardContent>
          <Typography variant="subtitle1" fontWeight="600" gutterBottom color="primary">
            <Psychology sx={{ verticalAlign: 'middle', mr: 1 }} />
            AI Insight
          </Typography>
          <Typography variant="body2" paragraph>
            Based on your current maturity and available resources, focusing on 
            operational transformations first will yield the fastest ROI. 
            Consider starting with T7 - Process Intelligence Transformation.
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            size="small"
            startIcon={<AutoGraph />}
            onClick={() => navigate('/iep/ai-optimizer')}
          >
            Get AI Recommendations
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default QuickActions;